import { apiGet, apiGetList, apiGetSearch } from '../../lib/api-client';

import type {
  ArchiveScope,
  CitationDto,
  ClaimDto,
  ConflictDto,
  ConflictStatus,
  EventDto,
  LifeState,
  PersonDto,
  PersonNameDto,
  PlaceDto,
  SearchResponseData,
  TreeContextData,
} from './people-types';

/**
 * Wołania `GET /api/v1/...` warstwy „osoby” (etap 6 część interfejsu, §9, §12.2) — WYŁĄCZNIE
 * przez `apiGet`/`apiGetList`/`apiGetSearch` (`../../lib/api-client.ts`), zgodnie z poleceniem
 * zadania „nie pisz własnego fetch”. Każda funkcja zwraca już rozpakowane `data` (ewentualnie z
 * `nextCursor` z `meta`) — komponenty nie znają koperty `{data, meta}`.
 */

export interface ListPage<T> {
  items: T[];
  nextCursor: string | null;
}

export interface FetchPeopleParams {
  cursor?: string;
  limit?: number;
  archiveScope?: ArchiveScope;
  lifeState?: LifeState;
  signal?: AbortSignal;
}

/** `GET /api/v1/people` (§12.2) — listowanie z kursorem, BEZ wyszukiwania po nazwie (schemat
 * zapytania `listPeopleQuerySchema` nie ma pola `q`; wyszukiwanie po nazwie idzie przez
 * `fetchPersonSearch`, `GET /api/v1/search`, patrz niżej). */
export async function fetchPeople(params: FetchPeopleParams = {}): Promise<ListPage<PersonDto>> {
  const search = new URLSearchParams();
  if (params.cursor !== undefined) {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params.archiveScope !== undefined) {
    search.set('archiveScope', params.archiveScope);
  }
  if (params.lifeState !== undefined) {
    search.set('lifeState', params.lifeState);
  }
  const query = search.toString();
  const response = await apiGetList<PersonDto>(
    `/api/v1/people${query.length > 0 ? `?${query}` : ''}`,
    params.signal,
  );
  return { items: response.data, nextCursor: response.meta.nextCursor };
}

export interface FetchPersonSearchParams {
  q: string;
  cursor?: string;
  signal?: AbortSignal;
}

export interface SearchPage extends SearchResponseData {
  nextCursor: string | null;
}

/**
 * `GET /api/v1/search?kinds=person` (§12.2, §5) — wyniki w dwóch grupach (rodzinne/badawcze).
 * `includeResearch` NIE jest tu przekazywane (domyślnie `false` po stronie serwera), więc grupa
 * `research` wraca zawsze PUSTA — §8.2/A13 „Warstwa badań domyślnie ukryta” zastosowane tu przez
 * analogię: lista osób nie ma dziś przełącznika ujawniającego warstwę badawczą (poza zamkniętym
 * zakresem tego zadania), więc po prostu nigdy o nią nie prosimy.
 */
export async function fetchPersonSearch(params: FetchPersonSearchParams): Promise<SearchPage> {
  const search = new URLSearchParams({ q: params.q, kinds: 'person' });
  if (params.cursor !== undefined) {
    search.set('cursor', params.cursor);
  }
  const response = await apiGetSearch<SearchResponseData>(
    `/api/v1/search?${search.toString()}`,
    params.signal,
  );
  return { ...response.data, nextCursor: response.meta.nextCursor };
}

export async function fetchPerson(id: string, signal?: AbortSignal): Promise<PersonDto> {
  const response = await apiGet<PersonDto>(`/api/v1/people/${id}`, signal);
  return response.data;
}

export interface FetchTreeContextParams {
  rootId: string;
  direction?: 'ancestors' | 'descendants' | 'all';
  depth?: number | 'all';
  /** §8.2 „Włączenie warstwy badań pokazuje proponowane połączenia […] zawsze z etykietą
   * statusu” — przekazywane wprost do `GET /tree?filters=research`. */
  includeResearch?: boolean;
  signal?: AbortSignal;
}

/** `GET /api/v1/tree` (§12.2) — wołanie ogólne, parametryzowane; {@link fetchPersonNeighborhood}
 * niżej jest jego wąskim, ustalonym wywołaniem dla zakładki „Relacje” profilu. Wydzielone osobno,
 * bo NAPRAWA zgłoszenia właściciela („Przypisuję partnerów i one się nie wyświetlają”) potrzebuje
 * TEGO SAMEGO żądania z dowolnym `rootId`/`direction`/`depth` też poza profilem osoby — patrz
 * `AssessRelationshipDialog`/`HiddenRelationshipsDialog`, które dociągają relacje UKRYTE przez
 * domyślny filtr wiarygodności (§8.2), żeby dać właścicielowi listę do potwierdzenia. */
export async function fetchTreeContext(params: FetchTreeContextParams): Promise<TreeContextData> {
  const search = new URLSearchParams({ rootId: params.rootId });
  search.set('direction', params.direction ?? 'all');
  search.set('depth', String(params.depth ?? 1));
  if (params.includeResearch === true) {
    search.set('filters', 'research');
  }
  const response = await apiGet<TreeContextData>(
    `/api/v1/tree?${search.toString()}`,
    params.signal,
  );
  return response.data;
}

/**
 * `GET /api/v1/tree?rootId=<id>&direction=all&depth=1&filters=research` — JEDYNE dziś dostępne
 * źródło:
 * 1. prezentacji preferowanej nazwy/dat urodzenia/zgonu TEJ osoby (§9 „Imiona i nazwiska”/
 *    „Daty”) — `GET /people/:id` (`fetchPerson` wyżej) zwraca WYŁĄCZNIE `label` surowe, bez
 *    informacji, czy/jak wybrane twierdzenie preferowanej nazwy rozstrzyga ją (brak
 *    `GET /people/:id/names`, patrz raport zadania);
 * 2. jednego pokolenia sąsiedztwa — rodzice, dzieci, partnerzy, rodzeństwo (§9 „Relacje”),
 *    razem z klasyfikacją wiarygodności każdej relacji (`tier`/`confidenceCodes`).
 *
 * `depth=1` jest MINIMALNĄ dozwoloną wartością (`MIN_TREE_DEPTH`, `domain/tree.ts`) — `depth=0`
 * nie istnieje w API. `direction='all'` łączy przodków/potomków JEDNEGO pokolenia z partnerami/
 * rodzeństwem w tym samym pokoleniu (`domain/tree.ts` komentarz), dokładnie odpowiadając czterem
 * kategoriom kart z wireframe'u §7.3 („Rodzice/Partnerzy/Dzieci”) plus rodzeństwem z §9.
 *
 * NAPRAWIONE (zgłoszenie właściciela: „Przypisuję partnerów i one się nie wyświetlają” — dotyczy
 * TEGO ekranu, nie tylko płótna drzewa): `filters=research` jest teraz ZAWSZE przekazywane. Bez
 * tego relacja/partner bez oceny `relationship_exists` (albo z oceną niższą niż potwierdzona)
 * były usuwane przez serwer PRZED dotarciem tutaj — karta partnera znikała CAŁKOWICIE z zakładki
 * „Relacje” zamiast pokazać się z widoczną etykietą statusu (`RelationCard`, `PersonRelationsTab.
 * tsx`, już renderuje `CONFIDENCE_TIER_LABELS` dla każdego poziomu — martwy kod bez tej zmiany,
 * bo serwer nigdy nie zwracał nic poza `confirmed`). Relacje `rejected` NADAL nie wracają — to
 * jedyny poziom wyłączony nawet z warstwą badań (§8.2, `isRelationshipVisibleWithResearch`).
 */
export async function fetchPersonNeighborhood(
  personId: string,
  signal?: AbortSignal,
): Promise<TreeContextData> {
  return fetchTreeContext({
    rootId: personId,
    direction: 'all',
    depth: 1,
    includeResearch: true,
    ...(signal === undefined ? {} : { signal }),
  });
}

export interface FetchPersonNamesParams {
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}

/** `GET /api/v1/people/:id/names` (§12.2, §9 „dowolna liczba nazw”, ADR 0015 — trasa nowo
 * dodana w tej turze, domykająca A06) — WSZYSTKIE warianty nazwy tej osoby, stronicowane
 * kursorem, w odróżnieniu od `namePresentation` z `GET /tree` (WYŁĄCZNIE wariant preferowany,
 * bez identyfikatora — patrz komentarz w `people-types.ts`, `TreeContextData`). Ta lista jest
 * jedynym źródłem pełnego katalogu wariantów (imiona, nazwisko, panieńskie, po małżeństwie,
 * pseudonim, zapis oryginalny, język, okres użycia — §9), niezależnie od tego, który (jeśli
 * którykolwiek) jest dziś wybrany jako preferowany. */
export async function fetchPersonNames(
  personId: string,
  params: FetchPersonNamesParams = {},
): Promise<ListPage<PersonNameDto>> {
  const search = new URLSearchParams();
  if (params.cursor !== undefined) {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  const response = await apiGetList<PersonNameDto>(
    `/api/v1/people/${personId}/names${query.length > 0 ? `?${query}` : ''}`,
    params.signal,
  );
  return { items: response.data, nextCursor: response.meta.nextCursor };
}

export interface FetchPersonClaimsParams {
  subjectEntityId: string;
  predicate: string;
  signal?: AbortSignal;
}

/** `GET /api/v1/claims?subjectEntityId=&predicate=&lifecycle=active` — WYŁĄCZNIE twierdzenia
 * AKTYWNE (§10.6 „Odrzucone twierdzenie […] nie usunięta osoba” — `rejected`/`superseded` nie są
 * pokazywane w profilu bieżącym, tylko przez `/history`, poza zamkniętym zakresem tego zadania).
 * Zwraca WSZYSTKIE strony (rzadko więcej niż kilka twierdzeń per predykat per osoba, więc
 * pojedyncze `limit` domyślne z backendu — 50 — wystarcza z ogromnym zapasem; nie ma tu
 * stronicowania w interfejsie). */
export async function fetchPersonClaims(params: FetchPersonClaimsParams): Promise<ClaimDto[]> {
  const search = new URLSearchParams({
    subjectEntityId: params.subjectEntityId,
    predicate: params.predicate,
    lifecycle: 'active',
  });
  const response = await apiGetList<ClaimDto>(`/api/v1/claims?${search.toString()}`, params.signal);
  return response.data;
}

/** `GET /api/v1/claims/selection?subjectEntityId=&predicate=` (naprawa raportu akceptacyjnego,
 * blokada B2, §11.1 wiersz `claim_selections`) — `claimId: null` znaczy „brak rozstrzygnięcia”
 * (§11.1), NIE błąd: `PersonPlaceFact.tsx` pokazuje wtedy WYŁĄCZNIE wartość, gdy istnieje
 * dokładnie jedno aktywne twierdzenie, inaczej „sporne”. */
export async function fetchClaimSelection(
  params: FetchPersonClaimsParams,
): Promise<{ claimId: string | null }> {
  const search = new URLSearchParams({
    subjectEntityId: params.subjectEntityId,
    predicate: params.predicate,
  });
  const response = await apiGet<{ claimId: string | null }>(
    `/api/v1/claims/selection?${search.toString()}`,
    params.signal,
  );
  return response.data;
}

export async function fetchClaim(id: string, signal?: AbortSignal): Promise<ClaimDto> {
  const response = await apiGet<ClaimDto>(`/api/v1/claims/${id}`, signal);
  return response.data;
}

/** `GET /api/v1/events?personId=` (§9 „Wydarzenia”) — lista zwraca WYŁĄCZNIE `type`/`label`/
 * `description`/znaczniki czasu rekordu (`eventsListResponseSchema` = lista `eventDtoSchema`,
 * BEZ uczestników/dat/miejsc wydarzenia — te są osobnymi twierdzeniami z podmiotem
 * `events.id`, `event_date`/`event_place`, poza zamkniętym zakresem tego zadania, patrz raport).
 * Sortowanie w interfejsie jest wg `createdAt` (kolejność wprowadzenia do archiwum), NIGDY
 * przedstawiane jako chronologia realnych wydarzeń — patrz `PersonTimelineTab.tsx`. */
export async function fetchPersonEvents(
  personId: string,
  signal?: AbortSignal,
): Promise<EventDto[]> {
  const search = new URLSearchParams({ personId });
  const response = await apiGetList<EventDto>(`/api/v1/events?${search.toString()}`, signal);
  return response.data;
}

/** `GET /api/v1/conflicts?subjectEntityId=&status=open` (§9 „Hipotezy/sprzeczności”, ADR 0009
 * pkt 4) — WYŁĄCZNIE konflikty otwarte; rozstrzygnięte są historią, poza zamkniętym zakresem. */
export async function fetchPersonConflicts(
  subjectEntityId: string,
  status: ConflictStatus = 'open',
  signal?: AbortSignal,
): Promise<ConflictDto[]> {
  const search = new URLSearchParams({ subjectEntityId, status });
  const response = await apiGetList<ConflictDto>(`/api/v1/conflicts?${search.toString()}`, signal);
  return response.data;
}

export async function fetchPlace(id: string, signal?: AbortSignal): Promise<PlaceDto> {
  const response = await apiGet<PlaceDto>(`/api/v1/places/${id}`, signal);
  return response.data;
}

export interface FetchPlacesParams {
  q?: string;
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
}

/** `GET /api/v1/places?q=` (§12.2 „q / nazwa, rodzaj, nadrzędne”) — używane przez
 * `ClaimFactDialog.tsx` do wyszukiwania miejsca istniejącego w archiwum przy edycji faktów
 * `birth_place`/`death_place`/`burial_place`/`residence_place` (ADR 0011). Nie tworzy nowych
 * miejsc — wybór jest WYŁĄCZNIE spośród miejsc już zarejestrowanych, dokładanie nowych miejsc
 * jest poza zamkniętym zakresem tego zadania (patrz raport zadania). */
export async function fetchPlaces(params: FetchPlacesParams = {}): Promise<ListPage<PlaceDto>> {
  const search = new URLSearchParams();
  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.cursor !== undefined) {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  const query = search.toString();
  const response = await apiGetList<PlaceDto>(
    `/api/v1/places${query.length > 0 ? `?${query}` : ''}`,
    params.signal,
  );
  return { items: response.data, nextCursor: response.meta.nextCursor };
}

export interface FetchCitationsParams {
  cursor?: string;
  limit?: number;
  /** Wyszukiwanie tekstowe po lokalizatorze cytowania I po tytule jego źródła (audyt UI,
   * znalezisko 2 — `GET /citations?q=`, `listCitationsQuerySchema` serwera). Naprawia sztywny
   * limit 20 bez możliwości zawężenia, na który wskazywał wcześniejszy komentarz tego pliku. */
  q?: string;
  /** Ogranicza listę do cytowań już użytych jako dowód aktywnego twierdzenia TEJ osoby (audyt UI,
   * znalezisko 2 — podpowiedź „cytowania już użyte przy tej osobie” na górze wyboru w
   * `CitationPickerField.tsx`). */
  subjectEntityId?: string;
  signal?: AbortSignal;
}

/** `GET /api/v1/citations` (§12.2) — używane przez `CitationPickerField.tsx`
 * (`ClaimFactDialog.tsx`) do wyboru ISTNIEJĄCEGO cytowania jako dowodu twierdzenia (§9 „Źródła i
 * cytowania”). NAPRAWIONE (audyt UI, znalezisko 2 — „podpięcie ISTNIEJĄCEGO cytowania działa
 * tylko dla pierwszych 20 cytowań w całym archiwum”): serwer ma teraz filtr tekstowy `q`
 * (lokalizator + tytuł źródła) i `subjectEntityId` (cytowania już użyte przy danej osobie) —
 * `CitationPickerField.tsx` łączy oba, zamiast polegać na jednej stronie bez możliwości
 * zawężenia. Tworzenie NOWEGO cytowania/źródła w tym dialogu nadal jest poza zamkniętym zakresem
 * tego zadania (wymagałoby też wyboru/utworzenia `source`, `GET/POST /sources` nie jest tu
 * używane). */
export async function fetchCitations(
  params: FetchCitationsParams = {},
): Promise<ListPage<CitationDto>> {
  const search = new URLSearchParams();
  if (params.cursor !== undefined) {
    search.set('cursor', params.cursor);
  }
  if (params.limit !== undefined) {
    search.set('limit', String(params.limit));
  }
  if (params.q !== undefined && params.q.length > 0) {
    search.set('q', params.q);
  }
  if (params.subjectEntityId !== undefined) {
    search.set('subjectEntityId', params.subjectEntityId);
  }
  const query = search.toString();
  const response = await apiGetList<CitationDto>(
    `/api/v1/citations${query.length > 0 ? `?${query}` : ''}`,
    params.signal,
  );
  return { items: response.data, nextCursor: response.meta.nextCursor };
}
